/**
 * Theme (light/dark) context. Single owner of the `data-theme` attribute + the `cortex_theme`
 * localStorage key. Wrap the whole app in <ThemeProvider> (so the login screen is themed too);
 * any component reads/toggles via useTheme().
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type ThemeState = { dark: boolean; toggle: () => void };

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [dark, setDark] = useState<boolean>(() => {
    try {
      const s = localStorage.getItem("cortex_theme");
      return s ? s === "dark" : true; // default dark
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("cortex_theme", dark ? "dark" : "light");
    } catch { /* ignore */ }
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <ThemeContext.Provider value={{ dark, toggle: () => setDark(d => !d) }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}
